import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown, FormControl, Spinner } from 'react-bootstrap';
import httpService from 'services/httpService.js';
import * as constants from 'Constants';

const StudentSearchableDropdown = ({
  submissionData,
  setSubmissionData,
  setSubmissionPath,
  setSubmission,
  submission,
  programId
}) => {
  const [filter, setFilter] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    if (submissionData === undefined || submissionData === null) {
      const stored = localStorage.getItem("submissionData");
      if (stored !== null && stored !== undefined) {
        const submissions = JSON.parse(stored);
        setSubmissionData(submissions)
        }
      }
  }, [submissionData]);

  const handleSelect = (student) => {
    setSelectedStudent(student);
    setSubmission(student);
    setSubmissionPath(student?.file_submitted || '');
    setDropdownOpen(false);
    setFilter(''); // Clear search after selection
    setSearchResults([]); // Clear search results
  };

  // Debounced search function
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const url = `${constants.base_url}/evalpro/submissions/${programId}?search_query=${encodeURIComponent(searchQuery)}`;
      const response = await httpService.get(url);

      // Extract submissions from the response
      const submissions = response.data?.submissions?.submissions?.results || [];
      setSearchResults(submissions);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [programId]);

  // Handle filter change with debouncing
  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilter(value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    if (value && value.length >= 2) {
      const timeout = setTimeout(() => {
        performSearch(value);
      }, 300); // 300ms debounce
      setSearchTimeout(timeout);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Determine which list to display
  const filteredStudents = filter && filter.length >= 2
    ? searchResults // Use search results when searching
    : submissionData?.filter(student => { // Use local data when not searching
        if(student === null || student === undefined) return false;
        const searchStr = `${student?.first_name} ${student?.last_name} ${student?.email}`.toLowerCase();
        return searchStr.includes(filter.toLowerCase());
      });

  return (
    <div>
      <Dropdown show={dropdownOpen} onToggle={() => setDropdownOpen(!dropdownOpen)}  style={{marginTop: '10px'}}>
        <h5 className="pl-3 pr-3">
          <span>Select Student: </span>
          <Dropdown.Toggle  id="studentDropdown" className="pl-4 pr-4 text-start fw-bold fs-4 text-white" overflow="hidden">
            {submission
              ? `${submission.first_name} ${submission.last_name} ${submission.email}\u00A0\u00A0`      // To add space between the email and the dropdown arrow
              : 'Select student'}
          </Dropdown.Toggle>
        </h5>

        <Dropdown.Menu style={{ maxHeight: '250px', overflowY: 'auto' }}>
          <FormControl
            autoFocus
            placeholder="Search by name, email, or roll number..."
            className="mx-3 my-2 w-auto"
            onChange={handleFilterChange}
            value={filter}
          />
          {isSearching ? (
            <div className="text-center p-3">
              <Spinner animation="border" size="sm" /> Searching...
            </div>
          ) : filteredStudents?.length > 0 ? (
            filteredStudents?.map((student) => (
              <Dropdown.Item
                key={student.id}
                onClick={() => handleSelect(student)}
              >
                {`${student.first_name} ${student.last_name} (${student.email})`}
              </Dropdown.Item>
            ))
          ) : filter && filter.length >= 2 ? (
            <Dropdown.Item disabled>No matching students found</Dropdown.Item>
          ) : (
            <Dropdown.Item disabled>Type at least 2 characters to search</Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default StudentSearchableDropdown;

